import { BrowserModule, Title } from '@angular/platform-browser';
import { ErrorHandler, NgModule } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LoginComponent } from './component/login/login.component';
import { DashboardComponent } from './component/dashboard/dashboard.component';
import { SidenavComponent } from './shared/sidenav/sidenav.component';
import { HeaderComponent } from './shared/header/header.component';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import * as Sentry from '@sentry/browser';
import { AuthInterceptor } from './service/httpservice/http.auth-interceptor';
import { SettingsComponent } from './component/settings/settings.component';
import { UsersComponent } from './component/settings/users/users.component';
import { RolesComponent } from './component/settings/roles/roles.component';
import { SmtpConfigurationComponent } from './component/settings/smtp-configuration/smtp-configuration.component';
import { DesignComponent } from './component/settings/design/design.component';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { JobsComponent } from './component/jobs/jobs.component';
import { OwnerInfoComponent } from './component/jobs/owner-info/owner-info.component';
import { AddEditOwnerInfoComponent } from './component/jobs/owner-info/add-edit-owner-info/add-edit-owner-info.component';
import { JobDetailsComponent } from './component/jobs/job-details/job-details.component';
import { JobStatusComponent } from './component/jobs/job-status/job-status.component';
import { environment } from '../environments/environment';
import { AngularFireModule } from '@angular/fire';
import { AngularFireStorageModule, AngularFireStorageReference, AngularFireUploadTask } from '@angular/fire/storage';
import { NgxMaskModule, IConfig } from 'ngx-mask';
import { ResetPasswordComponent } from './component/login/reset-password/reset-password.component';
import { DropzoneDirective } from './directive/dropzone/dropzone.directive';
import { CustomMinDirective } from './directive/validator/custom-min-validator.directive';
import { AutocompleteDropdownComponent } from './component/tools/shared/autocomplete-dropdown/autocomplete-dropdown.component';
import { UploaderComponent } from './shared/uploader/uploader.component';
import { UploadTaskComponent } from './shared/upload-task/upload-task.component';
import { ClickOutsideDirective } from './directive/click-outside/click-outside.directive';

import { DialogModule } from './module/dialog/dialog.module';
import { OrderModule } from 'ngx-order-pipe';
import { UnionLocalsComponent } from './component/settings/union-locals/union-locals.component';
import { SafeHtmlPipe } from './pipe/safe-html/safe-html.pipe';
import { CustomersComponent } from './component/jobs/customers/customers/customers.component';
import { EditCustomerComponent } from './component/jobs/customers/edit-customer/edit-customer/edit-customer.component';
import { InvoicesComponent } from './component/jobs/invoices/invoices.component';
import { ChatModalComponent } from './component/shared/chat-modal/chat-modal.component';
import { PdfGenerateComponent } from './component/shared/pdf-generate/pdf-generate.component';
import { ResetConfirmationModalComponent } from './component/shared/reset-confirmation-modal/reset-confirmation-modal.component';
import { ToolSettingsComponent } from './component/settings/tool-settings/tool-settings.component';
import { ToolSettingsTableComponent } from './component/settings/tool-settings/tool-settings-table/tool-settings-table.component';
import { ToolSettingsBarComponent } from './component/settings/tool-settings/tool-settings-bar/tool-settings-bar.component';
import { ActionButtonComponent } from './component/settings/tool-settings/action-button/action-button.component';
import { ToggleComponent } from './component/settings/tool-settings/toggle/toggle.component';
import { ToolSettingsPagingComponent } from './component/settings/tool-settings/tool-settings-paging/tool-settings-paging.component';
import { ToolModelListComponent } from './component/tools/tool-model-list/tool-model-list.component';
import { ToolInventoryComponent } from './component/tools/tool-inventory/tool-inventory.component';
import { ToolPageComponent } from './component/tools/tool-page/tool-page.component';
import { NavTabsComponent } from './component/shared/nav-tabs/nav-tabs.component';
import { CardComponent } from './component/shared/card/card.component';
import { ServicePromptComponent } from './component/shared/service-prompt/service-prompt.component';
import { SwitchCardComponent } from './component/shared/switch-card/switch-card.component';

import { AttachmentsCardComponent } from './component/shared/attachments-card/attachments-card.component';
import { ToolListComponent } from './component/tools/tool-list/tool-list.component';
import { ToolListPageComponent } from './component/tools/tool-list-page/tool-list-page.component';
import { NavListItemComponent } from './shared/sidenav/nav-list-item/nav-list-item.component';
import { NavListComponent } from './shared/sidenav/nav-list/nav-list.component';
import { ToolStatusComponent } from './component/tools/tool-status/tool-status.component';
import { DynamicComponentDirective } from './directive/dynamicComponent/dynamic-component.directive';
import { ToolSnapshotComponent } from './component/tools/tool-snapshot/tool-snapshot.component';
import { AttachmentItemComponent } from './component/shared/attachment-item/attachment-item.component';
import { ToolTransferComponent } from './component/tools/tool-transfer/tool-transfer.component';
import { ToolTransferSearchComponent } from './component/tools/tool-transfer/search/tool-transfer-search.component';
import { ToolIdTransferSearchComponent } from './component/tools/tool-transfer/tool-id-search/tool-id-transfer-search.component';
import { ToolTransferAssignmentComponent } from './component/tools/tool-transfer/assignments/tool-transfer-assignment.component';


import { ToolInventoryTransferComponent } from './component/tools/tool-inventory-transfer/tool-inventory-transfer.component';
import { ToolInventoryTransferSearchComponent } from './component/tools/tool-inventory-transfer/search/tool-inventory-transfer-search.component';
import { ToolInventoryIdTransferSearchComponent } from './component/tools/tool-inventory-transfer/tool-inventory-id-search/tool-inventory-id-transfer-search.component';
import { ToolInventoryTransferAssignmentComponent } from './component/tools/tool-inventory-transfer/assignments/tool-inventory-transfer-assignment.component';


import { ToolInventoryModalComponent } from './component/tools/tool-inventory-modal/tool-inventory-modal.component';
import { ToolInventoryModalSearchComponent } from './component/tools/tool-inventory-modal/search/tool-inventory-modal-search.component';
import { ToolInventoryIdModalSearchComponent } from './component/tools/tool-inventory-modal/tool-inventory-id-search/tool-inventory-id-modal-search.component';
import { ToolInventoryModalAssignmentComponent } from './component/tools/tool-inventory-modal/assignments/tool-inventory-modal-assignment.component';


import { AutoCompleteComponent } from './component/shared/autocomplete/autocomplete.component';
import { ToolHistoryComponent } from './component/tools/tool-history/tool-history.component';
import { ToolModelPageComponent } from './component/tools/tool-model-page/tool-model-page.component';
import { ToolRentalDetailsComponent } from './component/tools/shared/tool-rental-details/tool-rental-details.component';
import { JobSearchComponent } from './component/tools/shared/job-search-box/job-search-box.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { ToolTransferAutocompleteSearchComponent } from './component/tools/shared/tool-transfer-autocomplete-search/tool-transfer-autocomplete-search.component';
import { ToolTransferListComponent } from './component/tools/tool-transfer-list/tool-transfer-list.component';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { JobBoardComponent } from './component/job-pointe/job-board/job-board.component';
import { ManageJobsComponent } from './component/job-pointe/manage-jobs/manage-jobs.component';
import { ManageJobsStatusComponent } from './component/job-pointe/manage-jobs-status/manage-jobs-status.component';
import { EmployeesComponent } from './component/job-pointe/employees/employees.component';
import { ConfigurationComponent } from './component/job-pointe/configuration/configuration.component';
import { JobPointeSettingsComponent } from './component/settings/job-pointe-settings/job-pointe-settings.component';
import { JobPointeSettingsBarComponent } from './component/settings/job-pointe-settings/job-pointe-settings-bar/job-pointe-settings-bar.component';
import { JobPointeSettingsPagingComponent } from './component/settings/job-pointe-settings/job-pointe-settings-paging/job-pointe-settings-paging.component';
import { JobPointeSettingsTableComponent } from './component/settings/job-pointe-settings/job-pointe-settings-table/job-pointe-settings-table.component';
import { JobPointeListComponent } from './component/job-pointe/job-pointe-list/job-pointe-list.component';
import { EmployeeStatusComponent } from './component/job-pointe/employee-status/employee-status.component';
import { JobPointeActionButtonComponent } from './component/settings/job-pointe-settings/job-pointe-action-button/job-pointe-action-button.component';
import { JobPointeTransferComponent } from './component/job-pointe/job-pointe-transfer/job-pointe-transfer.component';
import { HubStatusComponent } from './component/job-pointe/hub-status/hub-status.component';
import { JobHistoryListComponent } from './component/job-pointe/job-history-list/job-history-list.component';
import { JobCountComponent } from './component/job-pointe/manage-jobs/job-count/job-count.component';
import { NgxIziToastModule } from 'ngx-izitoast';

import { PairsPipe } from './component/job-pointe/job-pointe-transfer/pairs.pipe';
import { JobBoardCardComponent } from './component/job-pointe/job-pointe-transfer/job-board-card/job-board-card.component';
import { ManageOwnerComponent } from './component/job-pointe/job-pointe-transfer/manage-owner/manage-owner.component';
import { ManageOwnerListComponent } from './component/job-pointe/manage-jobs/manage-owner-list/manage-owner-list.component';
import { ManageEmployeesListComponent } from './component/job-pointe/manage-jobs/manage-employees-list/manage-employees-list.component';
import { ManageJobsListComponent } from './component/job-pointe/manage-jobs/manage-jobs-list/manage-jobs-list.component';
import { EmployeeProfileComponent } from './component/job-pointe/employee-profile/employee-profile.component';
import { AddEmployeeJobComponent } from './component/job-pointe/job-pointe-transfer/add-employee-job/add-employee-job.component';
import { AddEmployeeListComponent } from './component/job-pointe/job-pointe-transfer/add-employee-list/add-employee-list.component';
import { AddEmployeeButtonComponent } from './component/job-pointe/job-pointe-transfer/add-employee-button/add-employee-button.component';
import { EditEmployeeAttributeComponent } from './component/job-pointe/job-pointe-transfer/edit-employee-attribute/edit-employee-attribute.component';
import { JobCardComponent } from './component/job-pointe/job-board/job-board-card/job-card.component';
import { JobBoardSidenavComponent } from './component/job-pointe/job-board/job-board-sidenav/job-board-sidenav.component';
import { JobCardListComponent } from './component/job-pointe/job-board/job-card-list/job-card-list.component';
import { JobEmployeeCardComponent } from './component/job-pointe/job-board/job-employee-card/job-employee-card.component';
import { JobFilterNavComponent } from './component/job-pointe/job-board/job-filter-nav/job-filter-nav.component';
import { JobFormatNavComponent } from './component/job-pointe/job-board/job-format-nav/job-format-nav.component';
import { JobColorCardComponent } from './component/job-pointe/job-board/job-color-card/job-color-card.component';
import { JobLegendNavComponent } from './component/job-pointe/job-board/job-legend-nav/job-legend-nav.component';
import { JobBoardProfileSidenavComponent } from './component/job-pointe/job-board/job-board-profile-sidenav/job-board-profile-sidenav.component';
import { JobBoardTagsComponent } from './component/job-pointe/job-board/job-board-tags/job-board-tags.component';
import { ConfigurationSettingsBarComponent } from './component/job-pointe/configuration/configuration-settings-bar/configuration-settings-bar.component';
import { ConfigurationSettingsTableComponent } from './component/job-pointe/configuration/configuration-settings-table/configuration-settings-table.component';
import { ConfigurationSettingsPagingComponent } from './component/job-pointe/configuration/configuration-settings-paging/configuration-settings-paging.component';
import { JobEmployeeTitleComponent } from './component/job-pointe/job-board/job-employee-title/job-employee-title.component';
import { JobBoardSortByComponent } from './component/job-pointe/job-board/job-board-sort-by/job-board-sort-by.component';
import { JobBoardGroupByComponent } from './component/job-pointe/job-board/job-board-group-by/job-board-group-by.component';
import { PaginationPipe } from './pipe/pagination.pipe';

// import { NgGUDCoreModule } from 'ng-vfor-lib';
import { NgDragDropModule } from 'ng-drag-drop';
// import { ExportAsModule } from 'ngx-export-as';

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    DashboardComponent,
    SidenavComponent,
    HeaderComponent,
    SettingsComponent,
    UsersComponent,
    RolesComponent,
    SmtpConfigurationComponent,
    DesignComponent,
    JobsComponent,
    OwnerInfoComponent,
    AddEditOwnerInfoComponent,
    JobDetailsComponent,
    JobStatusComponent,
    ResetPasswordComponent,
    DropzoneDirective,
    UploaderComponent,
    UploadTaskComponent,
    SwitchCardComponent,
    UnionLocalsComponent,
    SafeHtmlPipe,
    CustomersComponent,
    EditCustomerComponent,
    InvoicesComponent,
    ChatModalComponent,
    PdfGenerateComponent,
    ResetConfirmationModalComponent,
    ToolSettingsComponent,
    ToolSettingsTableComponent,
    ToolSettingsBarComponent,
    ActionButtonComponent,
    ToggleComponent,
    ToolSettingsPagingComponent,
    ToolModelListComponent,
    ToolPageComponent,
    NavTabsComponent,
    CardComponent,
    ServicePromptComponent,
    AttachmentsCardComponent,
    ToolListComponent,
    ToolListPageComponent,
    NavListItemComponent,
    NavListComponent,
    ToolStatusComponent,
    DynamicComponentDirective,
    SwitchCardComponent,
    ToolSnapshotComponent,
    AttachmentItemComponent,
    ToolTransferComponent,
    ToolTransferSearchComponent,
    ToolIdTransferSearchComponent,
    ToolInventoryTransferComponent,
    ToolInventoryModalComponent,
    ToolInventoryTransferSearchComponent,
    ToolInventoryIdTransferSearchComponent,
    ToolInventoryTransferAssignmentComponent,
    ToolInventoryModalSearchComponent,
    ToolInventoryIdModalSearchComponent,
    ToolInventoryModalAssignmentComponent,
    ClickOutsideDirective,
    CustomMinDirective,
    ToolTransferAssignmentComponent,
    AutocompleteDropdownComponent,
    AutoCompleteComponent,
    ToolHistoryComponent,
    ToolModelPageComponent,
    ToolRentalDetailsComponent,
    JobSearchComponent,
    ToolTransferAutocompleteSearchComponent,
    ToolTransferListComponent,
    JobBoardComponent,
    EmployeesComponent,
    ConfigurationComponent,
    JobPointeSettingsComponent,
    JobPointeSettingsBarComponent,
    JobPointeSettingsPagingComponent,
    JobPointeSettingsTableComponent,
    JobPointeListComponent,
    ManageJobsComponent,
    ManageJobsStatusComponent,
    HubStatusComponent,
    EmployeeStatusComponent,
    JobPointeActionButtonComponent,
    JobPointeTransferComponent,
    JobHistoryListComponent,
    PairsPipe,
    EmployeeProfileComponent,
    JobBoardCardComponent,
    ManageOwnerComponent,
    ManageOwnerListComponent,
    ManageEmployeesListComponent,
    ManageJobsListComponent,
    AddEmployeeJobComponent,
    JobCountComponent,
    AddEmployeeListComponent,
    AddEmployeeButtonComponent,
    EditEmployeeAttributeComponent,
    JobCardComponent,
    JobBoardSidenavComponent,
    JobCardListComponent,
    JobEmployeeCardComponent,
    JobFilterNavComponent,
    JobFormatNavComponent,
    JobColorCardComponent,
    JobLegendNavComponent,
    JobBoardProfileSidenavComponent,
    JobBoardTagsComponent,
    ConfigurationSettingsBarComponent,
    ConfigurationSettingsTableComponent,
    ConfigurationSettingsPagingComponent,
    JobEmployeeTitleComponent,
    JobBoardSortByComponent,
    JobBoardGroupByComponent,
    PaginationPipe,
    ToolInventoryComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    ReactiveFormsModule,
    FormsModule,
    AngularFireStorageModule,
    AngularFireModule.initializeApp(environment.config, 'cloud'),
    NgxMaskModule.forRoot(),
    OrderModule,
    DialogModule,
    NgSelectModule,
    NgxDaterangepickerMd.forRoot(),
    NgxIziToastModule,
    NgDragDropModule.forRoot(),
  ],
  providers: [
    Title,
    CurrencyPipe,
    { provide: Window, useValue: window },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: {
        handleError(err: any): void {
          Sentry.captureException(err.originalError || err);
          console.error(err);
        },
      },
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
